<template>
  <div class="scan-stage" role="status" aria-live="polite">
    <div class="scan-stage__header">
      <div class="scan-stage__label">
        <span v-if="slideLabel">{{ slideLabel }}</span>
        <span
          v-if="isWaitingForMshot"
          class="scan-stage__spinner"
          aria-label="Loading preview"
        />
      </div>
    </div>

    <div class="scan-stage__body">
    <!-- MAP -->
    <section v-show="activeSlide === 'map'" class="scan-slide scan-slide--map">
      <div class="map-wrap">
        <svg
          class="map-svg"
          :viewBox="`0 0 ${mapWidth} ${mapHeight}`"
          preserveAspectRatio="xMidYMid meet"
        >
          <path v-if="countryPath" :d="countryPath" class="map-nation map-nation--base" />
          <path
            v-if="statePath && mapDetailsVisible"
            :d="statePath"
            class="map-nation map-nation--state"
          />
        </svg>
        <!-- Full-stage radar — floods the whole scan card above the map. -->
        <div class="radar-bleed" :style="radarBleedStyle" aria-hidden="true">
          <div class="radar-bleed__ring" />
          <div class="radar-bleed__ring radar-bleed__ring--delay" />
          <div class="radar-bleed__glow" />
          <div class="radar-bleed__sweep" />
          <div class="radar-bleed__core" />
        </div>
        <div
          v-if="mapDetailsVisible && pinPoint"
          class="map-pin"
          :style="pctStyle(pinPoint)"
        >
          <span class="map-pin__halo" />
          <span class="map-pin__dot" />
        </div>
        <div
          v-if="mapDetailsVisible && pinPoint"
          class="map-brand-chip"
          :style="brandChipStyle"
        >
          <div class="map-brand-chip__text">
            <span>{{ brandName }}</span>
            <small v-if="locationLabel">{{ locationLabel }}</small>
          </div>
        </div>
        <template v-if="mapDetailsVisible">
          <div
            v-for="(comp, i) in competitorMarkers"
            :key="`comp-pin-${comp.name}-${i}`"
            class="map-pin map-pin--competitor"
            :style="{ ...pctStyle({ x: comp.pinX, y: comp.pinY }), '--i': i }"
          >
            <span class="map-pin__halo" />
            <span class="map-pin__dot" />
          </div>
          <div
            v-for="(comp, i) in competitorMarkers"
            :key="`comp-chip-${comp.name}-${i}`"
            class="map-competitor-chip"
            :style="{ ...pctStyle({ x: comp.chipX, y: comp.chipY }), '--i': i }"
          >
            <span>{{ comp.name }}</span>
            <small v-if="comp.label">{{ comp.label }}</small>
          </div>
        </template>
      </div>
    </section>

    <!-- SERVICES — fan deck like the original hero scan -->
    <section v-show="activeSlide === 'services'" class="scan-slide scan-slide--center">
      <div class="services-fan">
        <article
          v-for="(product, i) in displayProducts"
          :key="`${product.name}-${i}`"
          class="service-card"
          :class="{ 'service-card--selected': product.selected }"
          :style="fanCardStyle(product, i)"
        >
          <div class="service-card__photo">
            <img
              v-if="product.photo"
              :src="product.photo"
              alt=""
              class="service-card__img"
              loading="lazy"
            >
            <span v-else class="service-card__photo-placeholder">Service</span>
          </div>
          <div class="service-card__veil" />
          <div class="service-card__label">{{ product.name }}</div>
          <div v-if="product.selected" class="service-card__badge">Selected</div>
        </article>
      </div>
    </section>

    <!-- Healthgrades specialty search (mShots website scan) -->
    <section v-show="activeSlide === 'healthgrades'" class="scan-slide scan-slide--center">
      <div class="site-frame">
        <div class="site-frame__chrome">
          <span class="site-frame__dots"><i /><i /><i /></span>
          <div class="site-frame__url">{{ healthgradesDisplayUrl }}</div>
        </div>
        <div class="site-frame__viewport">
          <img
            v-if="hgSnapshotSrc"
            :src="hgSnapshotSrc"
            alt=""
            class="site-frame__shot"
            :class="{ 'site-frame__shot--scanning': activeSlide === 'healthgrades' && !isWaitingForMshot }"
            :style="hgScrollStyle"
          >
          <div
            v-if="!isWaitingForMshot"
            class="site-scan-bar"
            :style="{ top: `${hgScanTopPct}%` }"
          />
        </div>
      </div>
    </section>

    <!-- Brand doctor/providers page for specialty (mShots; skipped if no URL) -->
    <section v-show="activeSlide === 'doctor_profile'" class="scan-slide scan-slide--center">
      <div class="site-frame">
        <div class="site-frame__chrome">
          <span class="site-frame__dots"><i /><i /><i /></span>
          <div class="site-frame__url">{{ doctorProfileDisplayUrl }}</div>
        </div>
        <div class="site-frame__viewport">
          <img
            v-if="profileSnapshotSrc"
            :src="profileSnapshotSrc"
            alt=""
            class="site-frame__shot"
            :class="{ 'site-frame__shot--scanning': activeSlide === 'doctor_profile' }"
            :style="profileScrollStyle"
          >
          <div
            class="site-scan-bar"
            :style="{ top: `${profileScanTopPct}%` }"
          />
        </div>
      </div>
    </section>

    <!-- PERSONAS + QUERIES -->
    <section v-show="activeSlide === 'personas'" class="scan-slide scan-slide--personas">
      <div class="persona-grid">
        <article
          v-for="(group, i) in personaGroups"
          :key="group.persona.id || i"
          class="persona-card"
          :style="{ '--i': i }"
        >
          <div class="persona-card__header">
            <div class="persona-card__avatar">
              {{ initials(group.persona.name) }}
            </div>
            <div>
              <div class="persona-card__name">{{ group.persona.name || `Persona ${i + 1}` }}</div>
              <div v-if="group.persona.demographic?.location" class="persona-card__meta">
                {{ group.persona.demographic.location }}
              </div>
            </div>
          </div>
          <ul class="persona-card__queries">
            <li
              v-for="(query, qi) in group.queries"
              :key="query.id || qi"
              class="persona-card__query"
              :style="{ '--qi': qi }"
            >
              {{ query.query_text }}
            </li>
          </ul>
        </article>
      </div>
    </section>

    <!-- WEBSITE → PAGE SOURCE (flip card) -->
    <section
      v-show="activeSlide === 'website' || activeSlide === 'page_source'"
      class="scan-slide scan-slide--center"
    >
      <div class="flip-scene">
        <div
          class="flip-card"
          :class="{ 'is-flipped': activeSlide === 'page_source' }"
        >
          <div class="flip-card__face flip-card__face--front">
            <div class="site-frame">
              <div class="site-frame__chrome">
                <span class="site-frame__dots">
                  <i /><i /><i />
                </span>
                <div class="site-frame__url">{{ displayUrl }}</div>
              </div>
              <div class="site-frame__viewport">
                <img
                  v-if="snapshotSrc"
                  :src="snapshotSrc"
                  alt=""
                  class="site-frame__shot"
                  :class="{ 'site-frame__shot--scanning': activeSlide === 'website' && !isWaitingForMshot }"
                  :style="siteScrollStyle"
                >
                <div
                  v-if="activeSlide === 'website' && !isWaitingForMshot"
                  class="site-scan-bar"
                  :style="{ top: `${siteScanTopPct}%` }"
                />
              </div>
            </div>
          </div>

          <div class="flip-card__face flip-card__face--back">
            <div class="source-frame">
              <div class="source-frame__chrome">
                <div class="source-frame__title">
                  <span>PAGE SOURCE</span>
                  <small>view-source:{{ pageSourceHost }}</small>
                </div>
                <div class="source-frame__badge">Scanning page source…</div>
              </div>
              <div class="source-frame__viewport">
                <div
                  class="source-frame__code"
                  :style="pageSourceScrollStyle"
                >
                  <div
                    v-for="(line, i) in pageSourceLines"
                    :key="`src-${i}`"
                    class="source-line"
                  >
                    <span class="source-line__num">{{ i + 1 }}</span>
                    <span
                      class="source-line__code"
                      v-html="highlightSourceLine(line)"
                    />
                  </div>
                </div>
                <div
                  class="site-scan-bar"
                  :style="{ top: `${pageSourceScanTopPct}%` }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SCORE RINGS — Category Ownership + Sentiment -->
    <section v-show="activeSlide === 'score'" class="scan-slide scan-slide--score">
      <div class="score-slide__eyebrow">Analysis complete</div>
      <div class="score-slide__brand">
        <span class="score-slide__brand-dot" />
        <span>{{ brandName }}</span>
      </div>
      <div class="score-rings">
        <div
          v-for="metric in scoreMetrics"
          :key="metric.key"
          class="score-ring"
          :aria-label="metric.label"
        >
          <svg class="score-ring__svg" viewBox="0 0 240 240">
            <circle class="score-ring__track" cx="120" cy="120" r="104" />
            <circle
              class="score-ring__value"
              cx="120"
              cy="120"
              r="104"
              pathLength="100"
              :stroke-dasharray="`${metric.frac * 100} 100`"
            />
          </svg>
          <div class="score-ring__center">
            <div class="score-ring__number">{{ metric.displayed }}</div>
            <div class="score-ring__caption">{{ metric.label }}</div>
            <div class="score-ring__sub">Out of 100</div>
          </div>
        </div>
      </div>
    </section>
    </div>

    <div v-if="timelineStarted" class="scan-progress">
      <div class="scan-progress__bar" :style="{ width: `${progressPct}%` }" />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { geoAlbers, geoMercator, geoPath } from 'd3-geo'
import { feature } from 'topojson-client'
import countriesTopo from 'world-atlas/countries-110m.json'
import usStatesTopo from 'us-atlas/states-10m.json'
import { MEDICAL_STOCK_PHOTOS, stockPhotoFor } from '@/utils/medicalStockPhotos'

const store = useStore()

const NON_MAINLAND_US = new Set([
  'Alaska',
  'Hawaii',
  'Puerto Rico',
  'United States Virgin Islands',
  'Guam',
  'American Samoa',
  'Commonwealth of the Northern Mariana Islands'
])

const props = defineProps({
  brandName: { type: String, default: 'Your brand' },
  brandUrl: { type: String, default: '' },
  country: { type: String, default: 'United States' },
  /** Full entity region string for enrichment context (e.g. "United States, Tennessee, Memphis"). */
  region: { type: String, default: '' },
  personas: { type: Array, default: () => [] },
  queries: { type: Array, default: () => [] },
  products: { type: Array, default: () => [] },
  /** { healthgrades_url, doctor_profile_url, specialty, where_label } from enrichment */
  physicianPages: { type: Object, default: null },
  /** { url, title, lines[] } page-source / JSON-LD snippet from enrichment */
  pageSource: { type: Object, default: null },
  location: { type: Object, default: null },
  /** Competitor locations from enrichment: { name, label?, lat, lng } */
  competitors: { type: Array, default: () => [] },
  /** Competitor brand names from the assessment (passed into enrichment). */
  competitorNames: { type: Array, default: () => [] },
  /** Category Ownership score 0–100 */
  categoryOwnership: { type: Number, default: null },
  /** Sentiment (tone of summary) score 0–100 */
  sentiment: { type: Number, default: null },
  /** Total runtime in ms before emitting complete */
  durationMs: { type: Number, default: 38000 }
})

const emit = defineEmits(['complete'])

const mapWidth = 975
const mapHeight = 610
const countryPath = ref('')
const statePath = ref('')
const pinPoint = ref(null)
const competitorMarkers = ref([])
const radarOrigin = ref({ x: mapWidth / 2, y: mapHeight / 2 })
const activeSlide = ref('map')
const progressPct = ref(0)
const websiteLocalProgress = ref(0)
const scoreLocalProgress = ref(0)
/** Usable mShots URL once a real capture (not the generating placeholder) is ready. */
const snapshotSrc = ref('')
const hgSnapshotSrc = ref('')
const profileSnapshotSrc = ref('')
/** Active mShot wait gate: { target, hold, startedAt } */
const mshotWait = ref(null)
const hgLocalProgress = ref(0)
const profileLocalProgress = ref(0)
const pageSourceLocalProgress = ref(0)
/** Filled on mount when parent didn't already pass enrichment (e.g. page refresh). */
const localEnrichment = ref(null)
/** True after enrichment attempt finishes. */
const enrichmentSettled = ref(false)
/**
 * One-shot reveal for state + pins + chips.
 * Stays false while enrichment streams in so markers never mount at interim coords.
 */
const mapDetailsVisible = ref(false)
/** Progress bar / slide timeline only after map details are ready. */
const timelineStarted = ref(false)
let rafId = null
let startTime = 0
let pausedMs = 0
/** Populated after loader helpers are defined. */
let websiteLoader = null
let hgLoader = null
let profileLoader = null

const MSHOT_RETRY_MS = 3000
const MSHOT_WAIT_MAX_MS = 30000
const SITE_SLIDE_KEYS = ['healthgrades', 'doctor_profile', 'website']

const displayProducts = computed(() => {
  const lines = localEnrichment.value?.service_lines
  const base = (Array.isArray(lines) && lines.length)
    ? lines.slice(0, 4).map((line) => ({
      name: line.name,
      description: line.description || '',
      selected: !!line.selected,
      photo: line.photo || null
    }))
    : (props.products || []).map((p) => ({
      ...p,
      photo: p.photo || null
    }))

  // Prefer on-demand Pexels URLs from enrichment; fall back to curated stock pool.
  const used = new Set()
  return base.map((product, i) => {
    let photo = product.photo || stockPhotoFor(product.name, i)
    if (!product.photo) {
      let guard = 0
      while (used.has(photo) && guard < MEDICAL_STOCK_PHOTOS.length) {
        photo = stockPhotoFor(product.name, i + guard + 1)
        guard += 1
      }
    }
    if (photo) used.add(photo)
    return { ...product, photo }
  })
})

const displayPhysicianPages = computed(() => (
  localEnrichment.value?.physician_pages || props.physicianPages || null
))

const healthgradesUrl = computed(() => displayPhysicianPages.value?.healthgrades_url || '')
const doctorProfileUrl = computed(() => displayPhysicianPages.value?.doctor_profile_url || '')
const hasHealthgrades = computed(() => !!healthgradesUrl.value)
const hasDoctorProfile = computed(() => !!doctorProfileUrl.value)

const displayPageSource = computed(() => (
  localEnrichment.value?.page_source || props.pageSource || null
))

const pageSourceHost = computed(() => (
  displayPageSource.value?.url || displayUrl.value
))

const pageSourceLines = computed(() => {
  const lines = displayPageSource.value?.lines
  if (Array.isArray(lines) && lines.length) return lines
  return buildFallbackPageSourceLines()
})

const displayLocation = computed(() => localEnrichment.value?.location || props.location || null)

const displayCompetitors = computed(() => {
  const fromLocal = localEnrichment.value?.competitors
  if (Array.isArray(fromLocal) && fromLocal.length) {
    return fromLocal.filter((c) => c?.name && c.lat != null && c.lng != null)
  }
  return (props.competitors || []).filter((c) => c?.name && c.lat != null && c.lng != null)
})

const clampScore = (n) => {
  const v = Number(n)
  if (!Number.isFinite(v)) return null
  return Math.max(0, Math.min(100, Math.round(v)))
}

const ownershipTarget = computed(() => clampScore(props.categoryOwnership))
const sentimentTarget = computed(() => clampScore(props.sentiment))
const hasScore = computed(() => ownershipTarget.value != null || sentimentTarget.value != null)

const scoreMetrics = computed(() => {
  const metrics = []
  if (ownershipTarget.value != null) {
    metrics.push({
      key: 'ownership',
      label: 'Category Ownership',
      displayed: Math.round(scoreLocalProgress.value * ownershipTarget.value),
      frac: (scoreLocalProgress.value * ownershipTarget.value) / 100
    })
  }
  if (sentimentTarget.value != null) {
    metrics.push({
      key: 'sentiment',
      label: 'Sentiment',
      displayed: Math.round(scoreLocalProgress.value * sentimentTarget.value),
      frac: (scoreLocalProgress.value * sentimentTarget.value) / 100
    })
  }
  return metrics
})

const slides = computed(() => {
  const list = [
    { key: 'map', label: 'Mapping your market', weight: 3.2 },
    { key: 'services', label: 'Scanning services', weight: 3.2 }
  ]
  if (hasHealthgrades.value) {
    list.push({ key: 'healthgrades', label: 'Scanning Healthgrades', weight: 3.6 })
  }
  if (hasDoctorProfile.value) {
    list.push({ key: 'doctor_profile', label: 'Scanning physician pages', weight: 3.6 })
  }
  list.push(
    { key: 'personas', label: 'Reviewing personas & queries', weight: 4.0 },
    { key: 'website', label: 'Scanning your website', weight: 3.8 },
    { key: 'page_source', label: 'Scanning page source', weight: 3.8 }
  )
  if (hasScore.value) {
    list.push({ key: 'score', label: 'Analysis complete', weight: 3.6 })
  }
  return list
})

const slideLabel = computed(() => {
  if (activeSlide.value === 'score') return ''
  return slides.value.find((s) => s.key === activeSlide.value)?.label || 'Analyzing…'
})

const isWaitingForMshot = computed(() => !!mshotWait.value)

const locationLabel = computed(() => {
  const loc = displayLocation.value
  if (!loc) return ''
  return loc.label || [loc.city, loc.state].filter(Boolean).join(', ')
})

const pctStyle = (pt) => {
  if (!pt) return {}
  return {
    left: `${(pt.x / mapWidth) * 100}%`,
    top: `${(pt.y / mapHeight) * 100}%`
  }
}

/** Brand chip sits above the brand pin. */
const brandChipStyle = computed(() => {
  if (!pinPoint.value) return {}
  return pctStyle({ x: pinPoint.value.x, y: pinPoint.value.y - 52 })
})

/** Radar origin as % of the map slide (country center). */
const radarBleedStyle = computed(() => ({
  left: `${(radarOrigin.value.x / mapWidth) * 100}%`,
  top: `${(radarOrigin.value.y / mapHeight) * 100}%`
}))

/** Fan angles for service cards — outer cards swing wider, like the original. */
const fanAngles = computed(() => {
  const n = displayProducts.value.length
  if (n <= 1) return [0]
  const spread = n >= 5 ? 32 : n === 4 ? 26 : 20
  return displayProducts.value.map((_, i) => -spread + (i / (n - 1)) * spread * 2)
})

const fanCardStyle = (product, i) => {
  const ang = fanAngles.value[i] ?? 0
  return {
    '--ang': ang,
    '--i': i,
    '--delay': `${0.05 + Math.abs(ang) * 0.01}s`,
    zIndex: product.selected ? 20 : Math.round(10 - Math.abs(ang) / 8)
  }
}

const displayUrl = computed(() => {
  if (!props.brandUrl) return 'your-website.com'
  return props.brandUrl.replace(/^https?:\/\//i, '')
})

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')

const buildFallbackPageSourceLines = () => {
  const host = displayUrl.value
  const name = props.brandName || 'Medical Clinic'
  const city = displayLocation.value?.city || 'Local'
  const state = displayLocation.value?.state || ''
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    name,
    url: `https://${host}`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressRegion: state || undefined,
      addressCountry: 'US'
    }
  }
  const pretty = JSON.stringify(schema, null, 2).split('\n')
  // Split closing tags so vue-loader doesn't treat them as SFC end tags.
  const close = (tag) => `</${tag}>`
  return [
    '<!doctype html>',
    '<html lang="en">',
    '<head>',
    '  <meta charset="utf-8" />',
    `  <title>${name} - Care Near You</title>`,
    `  <meta name="description" content="${name} provides specialty care near ${city}." />`,
    '  <meta name="robots" content="index,follow,max-image-preview:large" />',
    '  <meta name="GPTBot" content="index" />',
    '  <meta name="ClaudeBot" content="index" />',
    `  ${close('head')}`,
    '<body>',
    '  <header class="site-nav">',
    `    <a class="brand" href="/">${name}</a>`,
    '    <nav>',
    '      <a href="/services">Services</a>',
    '      <a href="/physicians">Physicians</a>',
    '      <a href="/locations">Locations</a>',
    '      <a href="/portal">Patient portal</a>',
    '      <a class="btn" href="/appointments">Book an appointment</a>',
    `    ${close('nav')}`,
    `  ${close('header')}`,
    '  <main id="content">',
    `    <${'script'} type="application/ld+json">`,
    ...pretty.map((line) => `      ${line}`),
    `    ${close('script')}`,
    `  ${close('main')}`,
    close('body'),
    close('html')
  ]
}

/** Lightweight syntax coloring for the page-source editor face. */
const highlightSourceLine = (line) => {
  let html = escapeHtml(line)
  html = html.replace(
    /(&quot;[^&]*&quot;)(\s*:\s*)(&quot;[^&]*&quot;|-?\d+|true|false|null)/g,
    '<span class="tok-key">$1</span>$2<span class="tok-val">$3</span>'
  )
  html = html.replace(
    /(&lt;\/?)([\w:-]+)([^&]*?)(&gt;)/g,
    '<span class="tok-punct">$1</span><span class="tok-tag">$2</span><span class="tok-attr">$3</span><span class="tok-punct">$4</span>'
  )
  html = html.replace(
    /([\w:-]+)=(&quot;.*?&quot;)/g,
    '<span class="tok-attr-name">$1</span>=<span class="tok-str">$2</span>'
  )
  return html
}

const healthgradesDisplayUrl = computed(() => (
  healthgradesUrl.value.replace(/^https?:\/\//i, '') || 'www.healthgrades.com'
))

const doctorProfileDisplayUrl = computed(() => (
  doctorProfileUrl.value.replace(/^https?:\/\//i, '') || displayUrl.value
))

const mshotBaseFor = (pageUrl) => {
  if (!pageUrl) return ''
  const normalized = pageUrl.startsWith('http') ? pageUrl : `https://${pageUrl}`
  // Target must be fully encoded so its ?query doesn't collide with mShots ?w=
  return `https://s0.wp.com/mshots/v1/${encodeURIComponent(normalized)}?w=1280`
}

/** WordPress mShots base URL (no cache-buster). */
const snapshotBaseUrl = computed(() => mshotBaseFor(props.brandUrl))
const hgSnapshotBaseUrl = computed(() => mshotBaseFor(healthgradesUrl.value))
const profileSnapshotBaseUrl = computed(() => mshotBaseFor(doctorProfileUrl.value))

/** Classic mShots placeholders: 800×600 jpeg or 400×300 gif while generating. */
const isUsableMshot = (img) => {
  if (!img?.naturalWidth || !img.naturalHeight) return false
  if (img.naturalWidth === 800 && img.naturalHeight === 600) return false
  if (img.naturalWidth === 400 && img.naturalHeight === 300) return false
  return img.naturalWidth >= 640
}

/**
 * One serial loader per page URL.
 * - Retries add &r=N so the browser doesn't stick on a cached placeholder GIF
 * - On success, keep the URL that actually loaded (not the stale cached base)
 * - Single Image element; never overlap requests
 * - start() is sync-idempotent (warmAll + watch won't double-fire)
 */
const createMshotLoader = (key, getBase, assignSrc) => {
  let resolved = false
  let attemptCount = 0
  let timer = null
  let inFlight = false
  let started = false
  let img = null
  let activeBase = ''
  // Keep orphaned Image refs so GC doesn't NS_BINDING_ABORT in-flight loads.
  const retain = new Set()

  const stopTimer = () => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  const releaseRetained = (el) => {
    retain.delete(el)
  }

  const softAbandonCurrent = () => {
    if (!img) return
    const old = img
    old.onload = () => releaseRetained(old)
    old.onerror = () => releaseRetained(old)
    retain.add(old)
    img = null
    inFlight = false
  }

  const tryLoad = () => {
    if (resolved) return
    const base = getBase()
    if (!base || inFlight) return

    attemptCount += 1
    inFlight = true
    activeBase = base
    // Bust browser HTTP cache of mShots' temporary 400×300 / 800×600 placeholders.
    // First hit stays stable so a warm WP capture can be reused immediately.
    const requestUrl = attemptCount === 1 ? base : `${base}&r=${attemptCount}`

    if (!img) {
      img = new Image()
      img.decoding = 'async'
      img.referrerPolicy = 'no-referrer'
    }

    img.onload = () => {
      inFlight = false
      if (resolved) return
      if (!isUsableMshot(img)) return
      resolved = true
      started = false
      stopTimer()
      // Use the URL that returned a real capture (cache-busted retries included).
      assignSrc(requestUrl)
    }
    img.onerror = () => {
      inFlight = false
    }

    if (img.getAttribute('src')) {
      img.removeAttribute('src')
    }
    img.src = requestUrl
  }

  return {
    key,
    isResolved: () => resolved,
    isRunning: () => started || !!timer || inFlight,
    markResolved: () => {
      resolved = true
      started = false
      stopTimer()
    },
    start: () => {
      if (resolved) return
      const base = getBase()
      if (!base) return
      // Sync idempotent — closes the warmAll/watch race that aborted loads.
      if (started && activeBase === base) return
      if (activeBase && activeBase !== base) {
        softAbandonCurrent()
        stopTimer()
        attemptCount = 0
        resolved = false
      }
      started = true
      activeBase = base
      if (!timer) {
        tryLoad()
        timer = setInterval(tryLoad, MSHOT_RETRY_MS)
      } else if (!inFlight) {
        tryLoad()
      }
    },
    stop: () => {
      stopTimer()
      started = false
    },
    reset: () => {
      stopTimer()
      softAbandonCurrent()
      started = false
      resolved = false
      attemptCount = 0
      activeBase = ''
    }
  }
}

websiteLoader = createMshotLoader('website', () => snapshotBaseUrl.value, (url) => {
  snapshotSrc.value = url
})
hgLoader = createMshotLoader('healthgrades', () => hgSnapshotBaseUrl.value, (url) => {
  hgSnapshotSrc.value = url
})
profileLoader = createMshotLoader('doctor_profile', () => profileSnapshotBaseUrl.value, (url) => {
  profileSnapshotSrc.value = url
})

const loaderFor = (key) => {
  if (key === 'healthgrades') return hgLoader
  if (key === 'doctor_profile') return profileLoader
  if (key === 'website') return websiteLoader
  return null
}

/** Start loaders for any URLs we already know (idempotent). */
const warmAllMshots = () => {
  if (snapshotBaseUrl.value) websiteLoader.start()
  if (hgSnapshotBaseUrl.value) hgLoader.start()
  if (profileSnapshotBaseUrl.value) profileLoader.start()
}

const previousSlideKey = (key) => {
  const list = slides.value
  const idx = list.findIndex((s) => s.key === key)
  if (idx <= 0) return 'map'
  return list[idx - 1].key
}

const siteHasImage = (key) => {
  if (key === 'healthgrades') return !!hgSnapshotSrc.value
  if (key === 'doctor_profile') return !!profileSnapshotSrc.value
  if (key === 'website') return !!snapshotSrc.value
  return true
}

const siteSettled = (key) => !!loaderFor(key)?.isResolved()

const siteBaseUrl = (key) => {
  if (key === 'healthgrades') return hgSnapshotBaseUrl.value
  if (key === 'doctor_profile') return profileSnapshotBaseUrl.value
  if (key === 'website') return snapshotBaseUrl.value
  return ''
}

const markSiteSettled = (key) => {
  loaderFor(key)?.markResolved()
}

const startSiteRetries = (key) => {
  loaderFor(key)?.start()
}

const finishMshotWait = (now) => {
  const wait = mshotWait.value
  if (!wait) return
  markSiteSettled(wait.target)
  pausedMs += now - wait.startedAt
  mshotWait.value = null
}

/** If a site slide has no capture, keep showing the prior slide instead of an empty browser. */
const displaySlideFor = (key) => {
  if (!SITE_SLIDE_KEYS.includes(key)) return key
  if (siteHasImage(key)) return key
  if (key === 'doctor_profile' && siteHasImage('healthgrades')) return 'healthgrades'
  if (key === 'website') return 'personas'
  return previousSlideKey(key)
}

/** One-way scroll: image is 1.25× viewport tall; shift by the overflow fraction. */
const siteScrollStyle = computed(() => ({
  transform: `translate3d(0, ${-websiteLocalProgress.value * 20}%, 0)`
}))

const siteScanTopPct = computed(() => 6 + websiteLocalProgress.value * 88)

const hgScrollStyle = computed(() => ({
  transform: `translate3d(0, ${-hgLocalProgress.value * 20}%, 0)`
}))
const hgScanTopPct = computed(() => 6 + hgLocalProgress.value * 88)

const profileScrollStyle = computed(() => ({
  transform: `translate3d(0, ${-profileLocalProgress.value * 20}%, 0)`
}))
const profileScanTopPct = computed(() => 6 + profileLocalProgress.value * 88)

const pageSourceScrollStyle = computed(() => ({
  transform: `translate3d(0, ${-pageSourceLocalProgress.value * 42}%, 0)`
}))
const pageSourceScanTopPct = computed(() => 6 + pageSourceLocalProgress.value * 88)

const personaGroups = computed(() => {
  const personas = props.personas || []
  const queries = props.queries || []
  if (!personas.length) {
    return [{
      persona: { name: 'Audience persona', demographic: {} },
      queries: queries.slice(0, 3)
    }]
  }
  return personas.map((persona) => ({
    persona,
    queries: queries.filter((q) => q.persona_id === persona.id).slice(0, 3)
  }))
})

const initials = (name) => {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('')
}

const countryNameMap = {
  'United States': 'United States of America',
  Canada: 'Canada'
}

const isUnitedStates = (name) => {
  const n = (name || '').toLowerCase()
  return n === 'united states' || n === 'united states of america' || n === 'usa' || n === 'us'
}

const contiguousUsFeatures = () => {
  const states = feature(usStatesTopo, usStatesTopo.objects.states)
  return states.features.filter((f) => !NON_MAINLAND_US.has(f.properties.name))
}

const mainlandUsCollection = () => ({
  type: 'FeatureCollection',
  features: contiguousUsFeatures()
})

const usAlbers = () => geoAlbers()
  .parallels([29.5, 45.5])
  .rotate([96, 0])
  .center([0, 38])

const normalizeCountryName = (name) => {
  const raw = (name || '').trim()
  if (!raw) return ''
  if (isUnitedStates(raw)) return 'United States'
  if (/^canada$/i.test(raw)) return 'Canada'
  return raw
}

const countryFromRegion = (regionOrCountry) => {
  const text = (regionOrCountry || '').trim()
  if (!text) return ''
  if (/canada/i.test(text)) return 'Canada'
  if (/united states|\busa\b|\bu\.?s\.?\b/i.test(text)) return 'United States'
  return normalizeCountryName(text.split(',')[0])
}

const resolveCountryFeature = (countries, preferredName) => {
  const normalized = normalizeCountryName(preferredName)
  if (isUnitedStates(normalized)) return mainlandUsCollection()
  const atlasName = countryNameMap[normalized] || normalized
  return countries.features.find((f) => f.properties.name === atlasName) || null
}

const projectLatLng = (projection, lat, lng) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !projection) return null
  const projected = projection([lng, lat])
  if (!projected || !Number.isFinite(projected[0]) || !Number.isFinite(projected[1])) return null
  return { x: projected[0], y: projected[1] }
}

const US_STATE_ABBR = {
  al: 'Alabama', ak: 'Alaska', az: 'Arizona', ar: 'Arkansas', ca: 'California',
  co: 'Colorado', ct: 'Connecticut', de: 'Delaware', fl: 'Florida', ga: 'Georgia',
  hi: 'Hawaii', id: 'Idaho', il: 'Illinois', in: 'Indiana', ia: 'Iowa',
  ks: 'Kansas', ky: 'Kentucky', la: 'Louisiana', me: 'Maine', md: 'Maryland',
  ma: 'Massachusetts', mi: 'Michigan', mn: 'Minnesota', ms: 'Mississippi', mo: 'Missouri',
  mt: 'Montana', ne: 'Nebraska', nv: 'Nevada', nh: 'New Hampshire', nj: 'New Jersey',
  nm: 'New Mexico', ny: 'New York', nc: 'North Carolina', nd: 'North Dakota', oh: 'Ohio',
  ok: 'Oklahoma', or: 'Oregon', pa: 'Pennsylvania', ri: 'Rhode Island', sc: 'South Carolina',
  sd: 'South Dakota', tn: 'Tennessee', tx: 'Texas', ut: 'Utah', vt: 'Vermont',
  va: 'Virginia', wa: 'Washington', wv: 'West Virginia', wi: 'Wisconsin', wy: 'Wyoming',
  dc: 'District of Columbia'
}

/** Place competitor chips near pins without overlapping each other or the brand chip. */
const layoutCompetitorMarkers = (projection, brandPin) => {
  const CHIP_W = 148
  const CHIP_H = 40
  const pad = 18
  const brandObstacle = brandPin
    ? { x: brandPin.x, y: brandPin.y - 52 }
    : null

  const offsets = [
    [0, -48], [62, -26], [-62, -26], [78, 20], [-78, 20],
    [0, 54], [96, -48], [-96, -48], [54, 52], [-54, 52],
    [110, 8], [-110, 8]
  ]

  const inBounds = (x, y) => (
    x > pad + CHIP_W / 2 &&
    x < mapWidth - pad - CHIP_W / 2 &&
    y > pad + CHIP_H / 2 &&
    y < mapHeight - pad - CHIP_H / 2
  )

  const collides = (x, y, others) => others.some(
    (o) => Math.abs(o.x - x) < CHIP_W && Math.abs(o.y - y) < CHIP_H
  )

  const placed = []
  const markers = []

  for (const comp of displayCompetitors.value.slice(0, 5)) {
    const pin = projectLatLng(projection, Number(comp.lat), Number(comp.lng))
    if (!pin) continue

    const obstacles = [
      ...placed,
      ...(brandObstacle ? [brandObstacle] : []),
      ...(brandPin ? [brandPin] : [])
    ]

    let chosen = null
    for (const [dx, dy] of offsets) {
      const x = pin.x + dx
      const y = pin.y + dy
      if (inBounds(x, y) && !collides(x, y, obstacles)) {
        chosen = { x, y }
        break
      }
    }
    if (!chosen) {
      // Fall back to the least-overlapping offset even if slightly out of ideal spacing.
      let best = { x: pin.x, y: pin.y - 48, score: Infinity }
      for (const [dx, dy] of offsets) {
        const x = Math.min(mapWidth - pad - CHIP_W / 2, Math.max(pad + CHIP_W / 2, pin.x + dx))
        const y = Math.min(mapHeight - pad - CHIP_H / 2, Math.max(pad + CHIP_H / 2, pin.y + dy))
        const score = obstacles.reduce(
          (s, o) => s + 1 / (Math.hypot(o.x - x, o.y - y) + 1),
          0
        )
        if (score < best.score) best = { x, y, score }
      }
      chosen = { x: best.x, y: best.y }
    }

    placed.push(chosen)
    markers.push({
      name: comp.name,
      label: comp.label || [comp.city, comp.state].filter(Boolean).join(', '),
      pinX: pin.x,
      pinY: pin.y,
      chipX: chosen.x,
      chipY: chosen.y
    })
  }

  return markers
}

const projectBrandPin = (projection, lat, lng, fallbackFeature, path) => {
  const projected = projectLatLng(projection, lat, lng)
  if (projected) return projected
  if (fallbackFeature) {
    const c = path.centroid(fallbackFeature)
    return { x: c[0], y: c[1] }
  }
  return null
}

const buildMap = () => {
  const countries = feature(countriesTopo, countriesTopo.objects.countries)
  const loc = displayLocation.value || {}
  // Prefer the entity's selected region over LLM location.country (LLM often defaults to US).
  const preferredCountry = countryFromRegion(props.country) ||
    countryFromRegion(loc.country) ||
    'United States'
  const countryFeat = resolveCountryFeature(countries, preferredCountry)
  const lat = loc.lat == null ? null : Number(loc.lat)
  const lng = loc.lng == null ? null : Number(loc.lng)

  if (!countryFeat) {
    countryPath.value = ''
    statePath.value = ''
    pinPoint.value = null
    competitorMarkers.value = []
    return
  }

  // Always show the full country; highlight state on top when we know it.
  const usMainland = isUnitedStates(preferredCountry)
  const projection = usMainland
    ? usAlbers().fitExtent([[28, 28], [mapWidth - 28, mapHeight - 28]], mainlandUsCollection())
    : geoMercator().fitExtent([[40, 30], [mapWidth - 40, mapHeight - 30]], countryFeat)

  const path = geoPath(projection)
  const nationFeat = usMainland ? mainlandUsCollection() : countryFeat
  const nextCountryPath = path(nationFeat) || ''

  // Radar stays fixed at the country center — never jumps to the pin.
  const radarCenter = path.centroid(nationFeat)

  // State highlight is optional — only when we can resolve a real mainland US state.
  const stateFeat = resolveOptionalUsState(preferredCountry, loc.state)
  const nextStatePath = stateFeat ? (path(stateFeat) || '') : ''
  const nextPin = projectBrandPin(projection, lat, lng, stateFeat || countryFeat, path)
  const nextCompetitors = layoutCompetitorMarkers(projection, nextPin)

  // Assign once — never clear to [] first (that remounts chips and replays float-in).
  countryPath.value = nextCountryPath
  statePath.value = nextStatePath
  radarOrigin.value = { x: radarCenter[0], y: radarCenter[1] }
  pinPoint.value = nextPin
  competitorMarkers.value = nextCompetitors
}

const resolveOptionalUsState = (preferredCountry, stateName) => {
  if (!isUnitedStates(preferredCountry)) return null
  const raw = (stateName || '').toString().trim()
  if (!raw || raw.toLowerCase() === 'null' || raw.toLowerCase() === 'unknown') return null

  const asFull = US_STATE_ABBR[raw.toLowerCase()] || raw
  const states = feature(usStatesTopo, usStatesTopo.objects.states)
  const match = states.features.find((f) => {
    const name = (f.properties.name || '').toLowerCase()
    return name === asFull.toLowerCase()
  })
  if (!match || NON_MAINLAND_US.has(match.properties.name)) return null
  return match
}

const selectedServiceName = () => {
  const selected = (props.products || []).find((p) => p.selected)
  return selected?.name || props.products?.[0]?.name || ''
}

const ensureLocalEnrichment = async () => {
  const hasParentServices = (props.products || []).length >= 2
  const hasParentLocation = !!(props.location && (props.location.lat || props.location.city))
  const hasParentCompetitors = (props.competitors || []).length > 0
  const hasParentPhysicianPages = !!(props.physicianPages?.healthgrades_url)
  const hasParentPageSource = Array.isArray(props.pageSource?.lines) && props.pageSource.lines.length > 0
  const needsCompetitors = (props.competitorNames || []).length > 0 && !hasParentCompetitors
  if (
    hasParentServices &&
    hasParentLocation &&
    hasParentPhysicianPages &&
    hasParentPageSource &&
    !needsCompetitors
  ) return
  if (!props.brandName || !props.brandUrl) return

  try {
    const result = await store.dispatch('fetchScanEnrichment', {
      brand_name: props.brandName,
      website_url: props.brandUrl,
      selected_service: selectedServiceName(),
      region: props.region || props.country || '',
      competitors: props.competitorNames || []
    })
    if (result?.success) {
      localEnrichment.value = result
      warmAllMshots()
    }
  } catch (error) {
    console.warn('Scan enrichment unavailable:', error)
  }
}

const totalWeight = () => slides.value.reduce((sum, s) => sum + s.weight, 0)

const slideStartT = (key) => {
  const list = slides.value
  const idx = list.findIndex((s) => s.key === key)
  if (idx < 0) return 1
  const tw = totalWeight()
  return list.slice(0, idx).reduce((sum, s) => sum + s.weight, 0) / tw
}

const slideEndT = (key) => {
  const list = slides.value
  const idx = list.findIndex((s) => s.key === key)
  if (idx < 0) return 1
  const tw = totalWeight()
  return list.slice(0, idx + 1).reduce((sum, s) => sum + s.weight, 0) / tw
}

const slideAtProgress = (t) => {
  const list = slides.value
  const tw = totalWeight()
  let acc = 0
  for (const slide of list) {
    acc += slide.weight / tw
    if (t <= acc) return slide.key
  }
  return list[list.length - 1].key
}

const easeInOut = (p) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2)
const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3)

const localProgressFor = (key, t) => {
  if (!slides.value.some((s) => s.key === key)) return 0
  const start = slideStartT(key)
  const end = slideEndT(key)
  if (t <= start) return 0
  if (t >= end) return 1
  return easeInOut((t - start) / (end - start))
}

const tick = (now) => {
  if (!startTime) startTime = now

  // Hold on the prior slide until the next site capture is ready (or times out).
  if (mshotWait.value) {
    const { target, hold, startedAt } = mshotWait.value
    activeSlide.value = hold
    progressPct.value = slideEndT(hold) * 100
    if (siteHasImage(target) || now - startedAt >= MSHOT_WAIT_MAX_MS) {
      finishMshotWait(now)
    } else {
      rafId = requestAnimationFrame(tick)
      return
    }
  }

  const elapsed = now - startTime - pausedMs
  const t = Math.min(elapsed / props.durationMs, 1)
  const nextSlide = slideAtProgress(t)

  // Gate every site slide (HG, doctor page, brand site) — never enter an empty browser chrome.
  if (SITE_SLIDE_KEYS.includes(nextSlide) && !siteHasImage(nextSlide) && !siteSettled(nextSlide)) {
    if (!siteBaseUrl(nextSlide)) {
      markSiteSettled(nextSlide)
    } else {
      const hold = previousSlideKey(nextSlide)
      mshotWait.value = { target: nextSlide, hold, startedAt: now }
      activeSlide.value = hold
      progressPct.value = slideEndT(hold) * 100
      startSiteRetries(nextSlide)
      rafId = requestAnimationFrame(tick)
      return
    }
  }

  progressPct.value = t * 100
  activeSlide.value = displaySlideFor(nextSlide)

  websiteLocalProgress.value = localProgressFor('website', t)
  hgLocalProgress.value = localProgressFor('healthgrades', t)
  profileLocalProgress.value = localProgressFor('doctor_profile', t)
  pageSourceLocalProgress.value = localProgressFor('page_source', t)

  if (hasScore.value) {
    const scoreStart = slideStartT('score')
    if (t <= scoreStart) {
      scoreLocalProgress.value = 0
    } else {
      scoreLocalProgress.value = easeOutCubic(Math.min((t - scoreStart) / Math.max(1 - scoreStart, 0.001), 1))
    }
  } else {
    scoreLocalProgress.value = 0
  }

  if (t < 1) {
    rafId = requestAnimationFrame(tick)
  } else {
    emit('complete')
  }
}

const startTimeline = () => {
  if (timelineStarted.value) return
  timelineStarted.value = true
  startTime = 0
  pausedMs = 0
  rafId = requestAnimationFrame(tick)
}

const revealMapDetails = async () => {
  if (mapDetailsVisible.value) return
  enrichmentSettled.value = true
  buildMap()
  await nextTick()
  // Wait two frames so aspect-ratio / flex layout is committed before chips paint.
  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
  const lat = displayLocation.value?.lat
  const lng = displayLocation.value?.lng
  const hasCoords = lat != null && lng != null &&
    Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
  if (hasCoords && pinPoint.value) {
    mapDetailsVisible.value = true
  }
}

onMounted(async () => {
  // Country + radar first; pins/chips stay hidden until enrichment is final.
  warmAllMshots()
  buildMap()
  await ensureLocalEnrichment()
  warmAllMshots()
  await revealMapDetails()
  startTimeline()
})

// Rebuild geometry quietly while details are still hidden; never partial-reveal from the watch.
watch(
  () => ({
    country: props.country,
    lat: displayLocation.value?.lat ?? null,
    lng: displayLocation.value?.lng ?? null,
    state: displayLocation.value?.state ?? null,
    comps: displayCompetitors.value
      .map((c) => `${c.name}:${c.lat}:${c.lng}`)
      .join('|')
  }),
  () => {
    buildMap()
  }
)

watch(snapshotBaseUrl, (base, prev) => {
  if (base === prev) return
  if (!base) {
    snapshotSrc.value = ''
    websiteLoader.reset()
    return
  }
  // Only hard-reset when replacing a different non-empty URL.
  if (prev && prev !== base) {
    snapshotSrc.value = ''
    websiteLoader.reset()
  }
  websiteLoader.start()
})

watch(hgSnapshotBaseUrl, (base, prev) => {
  if (base === prev) return
  if (!base) {
    hgSnapshotSrc.value = ''
    hgLoader.reset()
    return
  }
  if (prev && prev !== base) {
    hgSnapshotSrc.value = ''
    hgLoader.reset()
  }
  hgLoader.start()
})

watch(profileSnapshotBaseUrl, (base, prev) => {
  if (base === prev) return
  if (!base) {
    profileSnapshotSrc.value = ''
    profileLoader.reset()
    return
  }
  if (prev && prev !== base) {
    profileSnapshotSrc.value = ''
    profileLoader.reset()
  }
  profileLoader.start()
})

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId)
  websiteLoader.stop()
  hgLoader.stop()
  profileLoader.stop()
})
</script>

<style scoped>
.scan-stage {
  --ink: #122035;
  --ocean: #2169a8;
  --teal: #1aacb0;
  --teal-bright: #22c9c2;
  --sand: #fdfcf8;
  --sand-100: #f7f4ec;
  --sand-200: #ece6d6;
  --text-400: #6a85a0;
  --text-600: #2e4a6a;

  position: relative;
  display: flex;
  flex-direction: column;
  min-height: min(72vh, 720px);
  border-radius: 20px;
  overflow: hidden;
  background:
    radial-gradient(120% 80% at 50% 0%, #e8f4f8 0%, transparent 55%),
    linear-gradient(160deg, var(--sand) 0%, var(--sand-100) 100%);
  border: 1px solid rgba(18, 32, 53, 0.08);
  box-shadow: 0 24px 60px rgba(18, 32, 53, 0.08);
}

.scan-stage__header {
  position: relative;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;
  padding: 20px 28px 12px;
  z-index: 8;
  /* Keep step label readable when slide content tries to stack underneath. */
  background: linear-gradient(
    180deg,
    rgba(253, 252, 248, 0.98) 0%,
    rgba(253, 252, 248, 0.92) 70%,
    rgba(253, 252, 248, 0) 100%
  );
}

.scan-stage__label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--teal);
  min-height: 1.2em;
  text-align: center;
}

.scan-stage__spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(26, 172, 176, 0.25);
  border-top-color: var(--teal);
  flex-shrink: 0;
  animation: scanLabelSpin 0.75s linear infinite;
}

@keyframes scanLabelSpin {
  to { transform: rotate(360deg); }
}

.scan-stage__body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.scan-slide {
  position: absolute;
  inset: 0;
  padding: 12px 28px 40px;
  animation: slideIn 0.55s ease;
}

.scan-slide--map {
  overflow: visible;
  padding: 8px 0 40px;
  /* No vertical slide — that read as the map "floating" before pins settle. */
  animation: mapSlideFade 0.45s ease;
}

@keyframes mapSlideFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.scan-slide--center {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Map — lock viewBox aspect so HTML pins align with the drawn geography (no letterbox drift). */
.map-wrap {
  position: relative;
  width: min(920px, 100%);
  max-height: 100%;
  aspect-ratio: 975 / 610;
  height: auto;
  margin: 0 auto;
  overflow: visible;
  z-index: 1;
}

.map-svg {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

/* Full-card radar flood — centered on country, far larger than the map. */
.radar-bleed {
  position: absolute;
  z-index: 2;
  width: 0;
  height: 0;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.radar-bleed__ring {
  position: absolute;
  left: 0;
  top: 0;
  width: min(120vmax, 1600px);
  height: min(120vmax, 1600px);
  margin: calc(min(120vmax, 1600px) / -2);
  border-radius: 50%;
  border: 2px solid rgba(34, 201, 194, 0.45);
  box-sizing: border-box;
  animation: pulseRingBleed 3.2s ease-out infinite;
}

.radar-bleed__ring--delay {
  width: min(165vmax, 2100px);
  height: min(165vmax, 2100px);
  margin: calc(min(165vmax, 2100px) / -2);
  animation-delay: 1.1s;
  border-color: rgba(34, 201, 194, 0.32);
}

.radar-bleed__glow {
  position: absolute;
  left: 0;
  top: 0;
  width: min(180vmax, 2400px);
  height: min(180vmax, 2400px);
  margin: calc(min(180vmax, 2400px) / -2);
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(34, 201, 194, 0.28) 0%,
    rgba(34, 201, 194, 0.12) 32%,
    rgba(34, 201, 194, 0.04) 55%,
    transparent 68%
  );
}

.radar-bleed__sweep {
  position: absolute;
  left: 0;
  top: 0;
  width: min(150vmax, 2000px);
  height: min(150vmax, 2000px);
  margin: calc(min(150vmax, 2000px) / -2);
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    rgba(34, 201, 194, 0.38) 45deg,
    rgba(34, 201, 194, 0.12) 70deg,
    transparent 95deg
  );
  mask-image: radial-gradient(circle, transparent 8%, #000 10%, #000 48%, transparent 50%);
  -webkit-mask-image: radial-gradient(circle, transparent 8%, #000 10%, #000 48%, transparent 50%);
  animation: sweepBleed 4s linear infinite;
}

.radar-bleed__core {
  position: absolute;
  left: 0;
  top: 0;
  width: 12px;
  height: 12px;
  margin: -6px;
  border-radius: 50%;
  background: rgba(33, 105, 168, 0.7);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.7);
}

@keyframes sweepBleed {
  to { transform: rotate(360deg); }
}

@keyframes pulseRingBleed {
  0% { opacity: 0.85; transform: scale(0.42); }
  100% { opacity: 0; transform: scale(1); }
}

.map-nation--base {
  fill: #e7eef4;
  stroke: #c5d3e0;
  stroke-width: 0.9;
}

.map-nation--state {
  fill: #7eb8e0;
  stroke: #2169a8;
  stroke-width: 1.5;
  animation: mapFadeIn 0.4s ease;
}

.map-pin {
  position: absolute;
  z-index: 4;
  width: 0;
  height: 0;
  pointer-events: none;
  transform: translate(-50%, -50%);
  animation: mapFadeIn 0.35s ease both;
  animation-delay: calc(var(--i, 0) * 0.08s);
}

.map-pin--competitor {
  z-index: 3;
}

@keyframes mapFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.map-pin__halo {
  position: absolute;
  left: 0;
  top: 0;
  width: 36px;
  height: 36px;
  margin: -18px;
  border-radius: 50%;
  background: rgba(33, 105, 168, 0.18);
  box-shadow: inset 0 0 0 2px rgba(33, 105, 168, 0.35);
}

.map-pin--competitor .map-pin__halo {
  width: 28px;
  height: 28px;
  margin: -14px;
  background: rgba(106, 133, 160, 0.16);
  box-shadow: inset 0 0 0 2px rgba(106, 133, 160, 0.35);
}

.map-pin__dot {
  position: absolute;
  left: 0;
  top: 0;
  width: 14px;
  height: 14px;
  margin: -7px;
  border-radius: 50%;
  background: var(--ocean);
  box-shadow: 0 0 0 3px #fff;
}

.map-pin--competitor .map-pin__dot {
  width: 10px;
  height: 10px;
  margin: -5px;
  background: #6a85a0;
  box-shadow: 0 0 0 2px #fff;
}

.map-brand-chip {
  position: absolute;
  z-index: 5;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--ocean);
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  padding: 10px 16px;
  border-radius: 999px;
  box-shadow: 0 12px 28px rgba(18, 32, 53, 0.2);
  max-width: min(42%, 260px);
  pointer-events: none;
  animation: mapFadeIn 0.35s ease both;
}

.map-competitor-chip {
  position: absolute;
  z-index: 4;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: rgba(255, 255, 255, 0.94);
  color: var(--ink);
  font-weight: 700;
  font-size: 12px;
  line-height: 1.2;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(18, 32, 53, 0.1);
  box-shadow: 0 8px 20px rgba(18, 32, 53, 0.12);
  max-width: min(36%, 200px);
  pointer-events: none;
  animation: mapFadeIn 0.35s ease both;
  animation-delay: calc(0.08s + var(--i, 0) * 0.08s);
}

.map-competitor-chip span,
.map-brand-chip__text span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-competitor-chip small {
  font-size: 10px;
  font-weight: 600;
  color: var(--text-400);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-brand-chip__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.map-brand-chip__text span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-brand-chip__text small {
  font-size: 11px;
  font-weight: 600;
  opacity: 0.8;
  margin-top: 2px;
}

/* Services — stacked fan deck (mirrors original hero scan) */
.services-fan {
  position: relative;
  width: min(640px, 100%);
  height: min(520px, 62vh);
}

.service-card {
  position: absolute;
  left: 50%;
  top: 42%;
  width: min(210px, 34vw);
  height: min(360px, 52vh);
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--sand-200);
  box-shadow: 0 28px 60px rgba(18, 32, 53, 0.2);
  transform-origin: 50% 175%;
  animation: fanCardIn 0.95s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: var(--delay, 0.05s);
  will-change: transform, opacity;
}

.service-card--selected {
  border-color: var(--teal);
  box-shadow:
    0 0 0 2px rgba(26, 172, 176, 0.35),
    0 28px 60px rgba(18, 32, 53, 0.22);
}

.service-card__photo {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(155deg, #d7e6f0 0%, #eaf4f8 42%, #e7dfc8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.service-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.service-card__photo-placeholder {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(18, 32, 53, 0.22);
}

.service-card__veil {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 46%;
  background: linear-gradient(0deg, transparent, rgba(18, 32, 53, 0.78));
  pointer-events: none;
}

.service-card__label {
  position: absolute;
  left: 16px;
  right: 16px;
  top: 16px;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: #fff;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.25);
}

.service-card__badge {
  position: absolute;
  left: 14px;
  bottom: 14px;
  display: inline-block;
  background: #e6f7f7;
  color: #117a7e;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 6px;
}

@keyframes fanCardIn {
  from {
    opacity: 0;
    transform: translate(-50%, calc(-50% + 110px)) rotate(0deg);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) rotate(calc(var(--ang) * 1deg));
  }
}

/* Personas — stay below the step label; scroll if cards are tall */
.scan-slide--personas {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-x: hidden;
  overflow-y: auto;
  padding-top: 8px;
}

.persona-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  width: min(1040px, 100%);
  margin: 0 auto;
  align-content: start;
  align-self: center;
  max-height: 100%;
}

.persona-card {
  background: #fff;
  border: 1px solid var(--sand-200);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 14px 34px rgba(18, 32, 53, 0.08);
  animation: rise 0.55s ease both;
  animation-delay: calc(var(--i) * 0.12s);
  min-width: 0;
}

.persona-card__header {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.persona-card__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #daeefa;
  color: var(--ocean);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}

.persona-card__name {
  font-weight: 700;
  color: var(--ink);
  font-size: 15px;
}

.persona-card__meta {
  font-size: 12px;
  color: var(--text-400);
  margin-top: 2px;
}

.persona-card__queries {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.persona-card__query {
  font-size: 13px;
  line-height: 1.4;
  color: var(--text-600);
  background: var(--sand);
  border: 1px solid var(--sand-200);
  border-radius: 10px;
  padding: 10px 12px;
  animation: rise 0.45s ease both;
  animation-delay: calc(0.2s + var(--qi) * 0.1s);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  overflow-wrap: anywhere;
}

@keyframes rise {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Website ↔ page-source flip */
.flip-scene {
  width: min(860px, 100%);
  perspective: 1600px;
}

.flip-card {
  position: relative;
  width: 100%;
  transform-style: preserve-3d;
  transition: transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
}

.flip-card.is-flipped {
  transform: rotateY(180deg);
}

.flip-card__face {
  width: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.flip-card__face--back {
  position: absolute;
  inset: 0;
  transform: rotateY(180deg);
}

.flip-card__face--back .source-frame {
  height: 100%;
}

/* Website */
.site-frame {
  width: 100%;
  background: #fff;
  border-radius: 16px;
  border: 1px solid var(--sand-200);
  box-shadow: 0 28px 70px rgba(18, 32, 53, 0.14);
  overflow: hidden;
}

.site-frame__chrome {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  background: var(--sand-100);
  border-bottom: 1px solid var(--sand-200);
}

.site-frame__dots {
  display: flex;
  gap: 6px;
}

.site-frame__dots i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--sand-200);
  display: block;
}

.site-frame__url {
  flex: 1;
  background: #fff;
  border: 1px solid var(--sand-200);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  color: var(--text-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.site-frame__viewport {
  position: relative;
  aspect-ratio: 16 / 10;
  background: var(--sand-100);
  overflow: hidden;
}

.site-frame__shot {
  width: 100%;
  height: 125%;
  object-fit: cover;
  object-position: top center;
  display: block;
  will-change: transform;
  transform: translate3d(0, 0, 0);
}

.site-frame__shot--scanning {
  transition: none;
}

.source-frame {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  background: #0f1724;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 28px 70px rgba(18, 32, 53, 0.28);
  overflow: hidden;
  color: #e2e8f0;
}

.source-frame__chrome {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(180deg, #1a2740 0%, #152033 100%);
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);
}

.source-frame__title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.source-frame__title span {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #7dd3fc;
}

.source-frame__title small {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.source-frame__badge {
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(34, 201, 194, 0.14);
  border: 1px solid rgba(34, 201, 194, 0.35);
  color: #5eead4;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.source-frame__viewport {
  position: relative;
  flex: 1;
  min-height: 0;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background:
    radial-gradient(120% 80% at 50% 0%, rgba(34, 201, 194, 0.08), transparent 55%),
    #0b1220;
}

.source-frame__code {
  padding: 14px 0 40px;
  font-family: "SF Mono", "JetBrains Mono", "Fira Code", ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.55;
  will-change: transform;
}

.source-line {
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 10px;
  padding: 0 14px;
}

.source-line__num {
  color: #475569;
  text-align: right;
  user-select: none;
}

.source-line__code {
  color: #cbd5e1;
  white-space: pre;
  overflow: hidden;
}

.source-line__code :deep(.tok-tag) { color: #7dd3fc; }
.source-line__code :deep(.tok-punct) { color: #64748b; }
.source-line__code :deep(.tok-attr) { color: #cbd5e1; }
.source-line__code :deep(.tok-attr-name) { color: #c4b5fd; }
.source-line__code :deep(.tok-str) { color: #86efac; }
.source-line__code :deep(.tok-key) { color: #93c5fd; }
.source-line__code :deep(.tok-val) { color: #fcd34d; }

.site-scan-bar {
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--teal-bright) 20%, var(--teal-bright) 80%, transparent);
  box-shadow: 0 0 18px rgba(34, 201, 194, 0.7);
  pointer-events: none;
  z-index: 2;
}

.site-scan-bar::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 120px;
  background: linear-gradient(0deg, rgba(34, 201, 194, 0.18), transparent 85%);
  pointer-events: none;
}

/* Score */
.scan-slide--score {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  background:
    radial-gradient(circle at 50% 42%, rgba(34, 201, 194, 0.1), transparent 52%),
    linear-gradient(135deg, #f5f9fc 0%, #e8f0f6 100%);
}

.score-slide__eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--teal);
}

.score-slide__brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--sand-200);
  border-radius: 999px;
  padding: 8px 18px 8px 10px;
  font-weight: 700;
  color: var(--ink);
  box-shadow: 0 10px 26px rgba(18, 32, 53, 0.1);
}

.score-slide__brand-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff;
  box-shadow: inset 0 0 0 3px var(--teal-bright);
}

.score-rings {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(16px, 4vw, 40px);
  width: 100%;
  flex-wrap: wrap;
}

.score-ring {
  position: relative;
  width: min(240px, 42vw);
  aspect-ratio: 1;
  flex: 0 0 auto;
}

.score-ring__svg {
  width: 100%;
  height: 100%;
  display: block;
  transform: rotate(-90deg);
  filter: drop-shadow(0 0 7px rgba(34, 201, 194, 0.35));
}

.score-ring__track,
.score-ring__value {
  fill: none;
  stroke-width: 12;
}

.score-ring__track {
  stroke: rgba(18, 32, 53, 0.1);
}

.score-ring__value {
  stroke: #1aacb0;
  stroke-linecap: round;
  transition: stroke-dasharray 0.05s linear;
}

.score-ring__center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.score-ring__number {
  font-size: clamp(48px, 9vw, 72px);
  font-weight: 800;
  letter-spacing: -0.05em;
  line-height: 1;
  color: #122035;
}

.score-ring__caption {
  margin-top: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(18, 32, 53, 0.55);
}

.score-ring__sub {
  margin-top: 3px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(18, 32, 53, 0.32);
}

.scan-progress {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 18px;
  height: 4px;
  border-radius: 4px;
  background: rgba(18, 32, 53, 0.08);
  overflow: hidden;
  z-index: 6;
}

.scan-progress__bar {
  height: 100%;
  background: linear-gradient(90deg, var(--ocean), var(--teal-bright));
  border-radius: 4px;
  transition: width 0.1s linear;
}

@media (max-width: 759px) {
  .services-fan {
    height: min(420px, 58vh);
  }

  .service-card {
    width: min(150px, 38vw);
    height: min(260px, 42vh);
  }

  .service-card__label {
    font-size: 14px;
    left: 12px;
    right: 12px;
    top: 12px;
  }

  .persona-grid {
    grid-template-columns: 1fr;
  }
}
</style>
